import React, { useState, useEffect } from 'react';
import { Table, Row, Col, Button, DatePicker, Input, Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { getPaymentConfirmedLoans, confirmPayments } from '../../services/loanService';
import { formatCurrency, formatDate, formatPercentage } from '../../utils/formatters';
import Page from '../../components/layout/Page';
import locale from 'antd/es/date-picker/locale/de_DE';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/de';
import { SearchOutlined, CheckOutlined } from '@ant-design/icons';
import MetricCard from '../../components/MetricCard';

const PaymentConfirmed = () => {
    const { t } = useTranslation();
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLoans, setSelectedLoans] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [filteredLoans, setFilteredLoans] = useState([]);
    const [paymentDate, setPaymentDate] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [metrics, setMetrics] = useState({
        totalAmount: 0,
        totalIncentive: 0,
        totalExtraBonus: 0
    });

    useEffect(() => {
        fetchLoans();
    }, []);

    useEffect(() => {
        const filtered = loans.filter(loan => {
            const searchFields = [
                loan.process_number,
                loan.customer?.name,
                loan.customer?.first_name,
                loan.primary_consultant?.first_name,
                loan.primary_consultant?.last_name,
                loan.bank?.name,
                loan.pooler?.name,
                loan.amount?.toString(),
            ].filter(Boolean).map(field => field.toLowerCase());

            return searchText === '' || searchFields.some(field => field.includes(searchText.toLowerCase()));
        });
        setFilteredLoans(filtered);
    }, [searchText, loans]);

    useEffect(() => {
        // Calculate totals for selected loans
        const selectedLoanData = filteredLoans.filter(loan => selectedLoans.includes(loan.id));
        const totals = selectedLoanData.reduce((acc, loan) => {
            const incentive = parseFloat(loan.incentive_amount || 0);
            const extraBonus = parseFloat(loan.extra_bonus || 0);
            return {
                totalIncentive: acc.totalIncentive + incentive,
                totalExtraBonus: acc.totalExtraBonus + extraBonus,
                totalAmount: acc.totalAmount + incentive + extraBonus
            };
        }, {
            totalAmount: 0,
            totalIncentive: 0,
            totalExtraBonus: 0
        });
        setMetrics(totals);
    }, [selectedLoans, filteredLoans]);

    const fetchLoans = async () => {
        setLoading(true);
        try {
            const data = await getPaymentConfirmedLoans();
            setLoans(data);
            setFilteredLoans(data);
        } catch (error) {
            message.error(t('errors.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentConfirm = async () => {
        if (!paymentDate) {
            message.error(t('errors.dateRequired'));
            return;
        }

        try {
            await confirmPayments(selectedLoans, paymentDate.format('YYYY-MM-DD'));
            message.success(t('loan.messages.paymentConfirmed'));
            setShowPaymentModal(false);
            setSelectedLoans([]);
            setPaymentDate(null);
            fetchLoans();
        } catch (error) {
            message.error(t('errors.updateError'));
        }
    };

    const columns = [
        {
            title: t('loan.fields.primaryConsultant'),
            dataIndex: 'primary_consultant',
            key: 'primary_consultant',
            render: (consultant) => `${consultant.first_name} ${consultant.last_name}` || '-'
        },
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },
        {
            title: t('loan.fields.customer'),
            dataIndex: 'customer',
            key: 'customer',
            render: (customer) => `${customer.name}${customer.first_name ? `, ${customer.first_name}` : ''}`
        },       
        {
            title: t('loan.fields.signatureDate'),
            dataIndex: 'signature_date',
            key: 'signature_date',
            render: (date) => date ? moment(date).format('DD.MM.YYYY') : '-'
        },        

        {
            title: t('loan.fields.bank'),
            dataIndex: 'bank',
            key: 'bank',
            render: (bank) => bank?.name || '-'
        },
        {
            title: t('loan.fields.pooler'),
            dataIndex: 'pooler',
            key: 'pooler',
            render: (pooler) => pooler?.name || '-'
        },
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (amount) => formatCurrency(amount)
        },
        {
            title: t('loan.fields.provision'),
            dataIndex: 'provision',
            key: 'provision',
            align: 'right',
            render: (provision) => formatPercentage(provision)
        },
        {
            title: t('loan.fields.invoiceNumber'),
            dataIndex: 'invoice_number',
            key: 'invoice_number',
            render: (number) => number || '-'
        },
        {
            title: t('loan.fields.incentive_amount'),
            dataIndex: 'incentive_amount',
            key: 'incentive_amount',
            align: 'right',
            render: (amount) => amount ? formatCurrency(amount) : '-'
        },
        {
            title: t('loan.fields.extraBonus'),
            dataIndex: 'extra_bonus',
            key: 'extra_bonus',
            align: 'right',
            render: (extra_bonus) => formatCurrency(extra_bonus)
        },           
        {
            title: t('loan.fields.invoice_comment'),
            dataIndex: 'invoice_comment',
            key: 'invoice_comment',
            render: (comment) => comment || '-'
        },
    ];

    const rowSelection = {
        selectedRowKeys: selectedLoans,
        onChange: (selectedRowKeys) => {
            setSelectedLoans(selectedRowKeys);
        }
    };

    return (
        <Page title={t('paymentConfirmed.title')}>
            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Row gutter={24}>
                        <Col xs={24} md={8}>
                            <MetricCard
                                title={t('paymentConfirmed.metrics.totalAmount')}
                                value={formatCurrency(metrics.totalAmount)}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <MetricCard
                                title={t('paymentConfirmed.metrics.totalCommission')}
                                value={formatCurrency(metrics.totalIncentive)}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <MetricCard
                                title={t('paymentConfirmed.metrics.totalExtraBonus')}
                                value={formatCurrency(metrics.totalExtraBonus)}
                            />
                        </Col>
                    </Row>
                </Col>

                <Col span={24}>
                    <Row gutter={24}>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item
                                label={t('loan.fields.paymentDate')}
                            >
                                <DatePicker
                                    locale={locale}
                                    value={paymentDate}
                                    onChange={setPaymentDate}
                                    placeholder={t('common.selectDate')}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item
                                style={{ marginBottom: 0 }}
                            >
                                <Input
                                    placeholder={t('common.search')}
                                    prefix={<SearchOutlined />}
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Col>

                <Col span={24}>
                    <div style={{ 
                        width: '100%',
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch'
                    }}>
                        <Table
                            dataSource={filteredLoans}
                            columns={columns}
                            rowKey="id"
                            loading={loading}
                            rowSelection={rowSelection}
                            scroll={{ x: 'max-content' }}
                        />
                    </div>
                </Col>

                <Col span={24}>
                    <Row justify="end">
                        <Col>
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                disabled={selectedLoans.length === 0 || !paymentDate}
                                onClick={handlePaymentConfirm}
                            >
                                {t('paymentConfirmed.confirmPayment')}
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Page>
    );
};

export default PaymentConfirmed;
